/**
 * /login —— 密码登录页(契约 login.ts)。
 *
 * 行为锚点(旧站 src/app/(portal)/login):
 * - 用户名 + 密码 + 验证码,全必填;无「记住我」(v1 无 remember 标志,
 *   TTL 沿用旧规则:玩家 24h / 生存管理 30d,服务端决定);
 * - 成功:服务端 Set-Cookie(evermc_session,HttpOnly)后整页跳转到
 *   return_to(同源校验过)或角色默认着陆页(管理 → /admin,玩家 → /user);
 * - 失败:bad credentials 不是 401,而是 validation_failed 且
 *   fields.username 携带「玩家昵称或密码错误。」;429 时 fields.username
 *   是锁定文案 —— 都直接展示服务端消息,不客户端重组;
 * - captcha_failed:challenge 已烧毁,取新验证码后才能重试;
 * - 已登录访问(guest-only):useGuestOnly 整页送走(旧站
 *   RedirectIfAuthenticated 中间件语义);
 * - ?registered=1:注册三步流程的着陆(契约:注册不自动登录),展示
 *   成功提示「注册成功,请登录」(旧站 register flash 文案);
 * - ?reset=1:密码重置成功的着陆(旧站 ResetPasswordController
 *   sendResetResponse:redirect /login + flash),展示「密码重置成功」。
 *
 * CSRF:登录是写请求,auth-client 经 csrf-client withRefreshRetry 携带
 * X-CSRF-Token;成功后 auth-client 自动 rebind(会话绑定已变)。
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { appUrl } from '@web-v4/router-utils';
import { Button, Form, FormField, Input } from '@web-v4/shared-ui';
import { authClient } from '../lib/clients';
import { toFormError } from '../lib/errors';
import type { FormErrorState } from '../lib/errors';
import { loginLanding } from '../lib/navigation';
import { useGuestOnly } from '../lib/guest-only';
import { PortalLayout } from '../components/PortalLayout';
import { CaptchaControl, useCaptcha } from '../components/CaptchaField';

export function LoginPage() {
  useGuestOnly();
  const captcha = useCaptcha();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<FormErrorState>({ fields: {} });

  // 注册成功着陆(契约 register.ts:NO auto-login → 登录页 + 成功提示)。
  const registered =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('registered') === '1';

  // 密码重置成功着陆(旧站 redirect /login + flash「密码重置成功」)。
  const reset =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('reset') === '1';

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void event;
    if (captcha.challenge === undefined) {
      setError({ form: '验证码尚未加载完成,请稍后。', fields: {} });
      return;
    }
    setSubmitting(true);
    setError({ fields: {} });
    authClient
      .login({
        username: username.trim(),
        password,
        captcha_id: captcha.challenge.captcha_id,
        captcha_answer: answer.trim(),
      })
      .then((data) => {
        // 整页跳转:session cookie 由服务端写好,目标页(可能是另一个
        // app)以全新文档加载,CSRF bootstrap 随之重新绑定。
        window.location.assign(loginLanding(data, window.location.search));
      })
      .catch((cause: unknown) => {
        const next = toFormError(cause);
        setError(next);
        // 每次校验尝试都烧毁 challenge(契约 captcha.ts):无论失败
        // 原因,重试前换新验证码。
        captcha.reload();
        setAnswer('');
        setSubmitting(false);
      });
  };

  return (
    <PortalLayout
      mainClassName="wv-portal-main--narrow"
      shellClassName="wv-portal-shell--cover"
      showNavLinks={false}
    >
      <div className="wv-portal-card">
        <h1 className="wv-portal-card__title">欢迎回来</h1>
        <p className="wv-portal-card__subtitle">请输入你的账户信息</p>

        {registered && (
          <p className="wv-portal-notice wv-portal-notice--success" role="status">
            注册成功,请登录
          </p>
        )}
        {reset && (
          <p className="wv-portal-notice wv-portal-notice--success" role="status">
            密码重置成功
          </p>
        )}

        <Form error={error.form} onSubmit={onSubmit}>
          <FormField label="玩家昵称" required error={error.fields.username}>
            <Input
              type="text"
              name="username"
              autoComplete="username"
              autoFocus
              required
              value={username}
              disabled={submitting}
              onChange={(event) => setUsername(event.target.value)}
            />
          </FormField>

          <FormField label="密码" required error={error.fields.password}>
            <Input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              disabled={submitting}
              onChange={(event) => setPassword(event.target.value)}
            />
          </FormField>

          <FormField label="验证码" required>
            <CaptchaControl
              captcha={captcha}
              answer={answer}
              onAnswerChange={setAnswer}
              error={error.fields.captcha}
              disabled={submitting}
            />
          </FormField>

          <div className="wv-portal-card__actions">
            <Button
              type="submit"
              fullWidth
              loading={submitting}
              disabled={captcha.challenge === undefined}
            >
              登录
            </Button>
          </div>
        </Form>

        <div className="wv-portal-card__links">
          <a href={appUrl('portal', '/password')}>忘记密码?</a>
          <span>
            还没有账号?<a href={appUrl('portal', '/register')}>注册</a>
          </span>
        </div>

        <p className="wv-portal-card__fineprint">
          成为本站用户即代表您接受我们的<a href={appUrl('portal', '/terms')}>服务条款</a>
        </p>
      </div>
    </PortalLayout>
  );
}
